import { Button, Card, Input } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import ContentWrapper from "../../../../../common/modules/wrappers/ContentWrapper";
import ComponentWithPermission from "../../../../../common/security/authorization/ComponentWithPermission";
import { Permission } from "../../../../../common/security/authorization/enums";
import { useFilterSearchParams } from "../../../../../common/utils/hooksUtils";
import messageUtils from "../../../../../common/utils/messageUtils";
import { URLQuery } from "../../../../../common/utils/queryUtils";
import { validationConstants, validationFunctions } from "../../../../../common/utils/validationUtils";
import { getEnumerationsActions } from "../../../../enumerations/ducks";
import { CommissionsSettingsRank } from "../../types";
import { CommissionsSettingsRankModalForm } from "../forms/CommissionsSettingsRankModalForm";
import { CommissionsSettingsRanksTableView } from "../list/CommissionsSettingsRanksTableView";

export const CommissionsSettingsRanksListContainer = () => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rankToUpdate, setRankToUpdate] = useState<CommissionsSettingsRank>();

  const navigate = useNavigate();
  const keywordSearchParam = useFilterSearchParams().keyword;

  const dispatch = useDispatch();
  const actions = useMemo(
    () => bindActionCreators({ getEnumerations: getEnumerationsActions.request }, dispatch),
    [dispatch]
  );

  const handleSearchSubmit = (keyword: string) => {
    if (validationFunctions.validateSearchKeyword(keyword)) {
      navigate(
        {
          search: URLQuery.searchParamsToString({
            searchParams: { keyword }
          })
        },
        { replace: true }
      );
    } else {
      messageUtils.errorMessage(
        t("validation.size", {
          min: validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
          max: validationConstants.SEARCH_KEYWORD_MAX_LENGTH
        })
      );
    }
  };

  const handleCreateRank = () => {
    setRankToUpdate(undefined);
    setIsModalOpen(true);
  };

  const handleUpdateRank = (rank: CommissionsSettingsRank) => {
    setRankToUpdate(rank);
    setIsModalOpen(true);
  };

  const handleFormCancel = () => {
    setIsModalOpen(false);
    setRankToUpdate(undefined);
    actions.getEnumerations();
  };

  return (
    <ContentWrapper>
      <Card
        className="card-box"
        title={<h2>{t("commissions.rank.titles.list")}</h2>}
        extra={
          <>
            <ComponentWithPermission permissions={[Permission.TOP_AGENT_COMMISSIONS_MANAGE]}>
              <Button
                className="margin-right-small"
                type="primary"
                icon={<AntIcon type="plus" />}
                onClick={handleCreateRank}
              >
                {t("commissions.rank.actions.createRank")}
              </Button>
            </ComponentWithPermission>

            <Input.Search
              style={{ width: 350 }}
              enterButton
              allowClear
              placeholder={t("commissions.rank.helpers.searchHint")}
              defaultValue={keywordSearchParam}
              onSearch={handleSearchSubmit}
            />
          </>
        }
      >
        <CommissionsSettingsRanksTableView onUpdateRank={handleUpdateRank} />
      </Card>

      <CommissionsSettingsRankModalForm rank={rankToUpdate} isOpen={isModalOpen} onFormCancel={handleFormCancel} />
    </ContentWrapper>
  );
};
